import { allOf, formResult, signalEquals } from './equation.js';
import { RUNTIME_EVENTS, SCRATCHPAD_EVENTS } from './graph.js';
import { startConversationOpening } from './template-openings.js';
/**
 * The new owner lead: call an owner who asked about property management. Ask
 * four quick questions for the sales team, book a sales call, then collect
 * any last details for the team. An owner who wants a person, or who is not
 * interested, is noted for the team.
 */
const START_ID = '00000000-0000-4000-8000-00000006d001';
const GREET_ID = '00000000-0000-4000-8000-00000006a001';
const DISCOVERY_ID = '00000000-0000-4000-8000-00000006f002';
const SCHEDULE_ID = '00000000-0000-4000-8000-00000006c001';
const WRAP_UP_ID = '00000000-0000-4000-8000-00000006c002';
const END_SIGN_OFF_ID = '00000000-0000-4000-8000-00000006a002';
const END_HANDOFF_ID = '00000000-0000-4000-8000-00000006a003';
const END_DNC_ID = '00000000-0000-4000-8000-00000006a005';
const edgeId = (n) => `00000000-0000-4000-8000-00000006e${String(n).padStart(3, '0')}`;
const OPENING = startConversationOpening({
    startNodeId: START_ID,
    entryEdgeId: edgeId(1),
    exitEdgeId: edgeId(20),
    toNodeId: GREET_ID,
});
export const NEW_OWNER_LEAD_GRAPH_TEMPLATE = {
    name: 'New owner lead',
    persona: 'You are Sam, an automated agent for Super, a property management ' +
        'company. You call owners who asked about property management services, ' +
        'and you help schedule a time for them with the sales team. Be warm, ' +
        'natural, and concise: one or two short spoken sentences per turn, one ' +
        'question per turn.',
    active: true,
    knowledgeBaseIds: [],
    watchEvents: [
        SCRATCHPAD_EVENTS.DO_NOT_CALL,
        SCRATCHPAD_EVENTS.NOT_INTERESTED,
        SCRATCHPAD_EVENTS.HANDOFF_REQUESTED,
    ],
    nodes: [
        OPENING.node,
        {
            id: GREET_ID,
            type: 'say',
            title: 'Greeting',
            position: 1,
            text: 'Hi, this is Sam with Super, following up on your property ' +
                "management inquiry. I'm our automated agent to help schedule a time " +
                "for you and our sales team, and I'll share these notes with them too.",
        },
        {
            id: DISCOVERY_ID,
            type: 'form',
            tools: [],
            knowledgeBaseIds: [],
            excludedKnowledgeBaseIds: [],
            title: 'Owner lead discovery',
            position: 2,
            items: [
                {
                    kind: 'question',
                    id: 'property_zip',
                    text: 'Before we schedule, can I just grab a couple quick pieces of ' +
                        "info for our team to prepare for the call? What's the zip code " +
                        "of the property you'd like managed?",
                    // Free text so "not sure" passes; nothing in the flow reads it.
                    answerType: { kind: 'free_text' },
                    required: false,
                    mode: 'verbatim',
                    depth: 0,
                },
                {
                    kind: 'question',
                    id: 'unit_count',
                    text: 'How many units is it? One, two, three, or more?',
                    // Free text so "a few" passes; nothing in the flow reads it.
                    answerType: { kind: 'free_text' },
                    required: false,
                    mode: 'verbatim',
                    depth: 0,
                },
                {
                    kind: 'question',
                    id: 'management_timeline',
                    text: 'Ask who is currently managing the property, and when they are ' +
                        'looking for new management.',
                    answerType: { kind: 'free_text' },
                    required: false,
                    mode: 'instruction',
                    depth: 0,
                },
                {
                    kind: 'question',
                    id: 'property_situation',
                    text: 'Last question before we look at schedules! Is the property ' +
                        'currently your primary home, rented, or vacant?',
                    answerType: {
                        kind: 'options',
                        options: ['rented', 'vacant', 'primary', 'other'],
                    },
                    required: false,
                    mode: 'verbatim',
                    depth: 0,
                },
            ],
        },
        {
            id: SCHEDULE_ID,
            type: 'conversation',
            title: 'Schedule sales call',
            position: 3,
            prompt: 'Open by asking what time works best to connect with our sales ' +
                'team — e.g. "What day and time works for you?" Then book the ' +
                'time they pick.',
            tools: [{ tool: 'OWNER_MEETING_SCHEDULING' }],
            knowledgeBaseIds: [],
            excludedKnowledgeBaseIds: [],
            speaksOnEntry: true,
            // An owner who would rather hear from a person than pick a time leaves
            // for the handoff instead of being pushed through the calendar.
            signals: [
                {
                    id: 'wants_follow_up',
                    text: 'The caller asked for someone from the team to follow up instead ' +
                        'of booking a time now',
                },
            ],
        },
        {
            id: WRAP_UP_ID,
            type: 'conversation',
            title: 'Wrap up',
            position: 4,
            prompt: "Open by confirming they're all set for the meeting (e.g. " +
                '"Great, you\'re all booked!"). Then ask if they have any ' +
                'questions or additional details they want our sales team to ' +
                'know about to prepare for the call.',
            tools: [],
            knowledgeBaseIds: [],
            excludedKnowledgeBaseIds: [],
            speaksOnEntry: true,
            signals: [
                {
                    id: 'no_more_details',
                    text: 'The caller said they have no more questions or details for the ' +
                        'sales team',
                },
            ],
        },
        {
            id: END_SIGN_OFF_ID,
            type: 'end_conversation',
            title: 'End conversation',
            position: 5,
            signOff: {
                mode: 'exact',
                promptText: '',
                exactText: "Our team's looking forward to discussing our management services " +
                    'with you. Thank you for considering Super!',
            },
        },
        {
            id: END_HANDOFF_ID,
            type: 'end_conversation',
            title: 'End conversation',
            position: 6,
            signOff: {
                mode: 'exact',
                promptText: '',
                exactText: "Understood. I'll update the notes for the team so they know. Have a " +
                    'great day.',
            },
        },
        {
            id: END_DNC_ID,
            type: 'end_conversation',
            title: 'End conversation',
            position: 7,
            signOff: {
                mode: 'exact',
                promptText: '',
                exactText: "Understood. I'll update your contact so you don't get more calls " +
                    'from us. Have a good day.',
            },
        },
    ],
    edges: [
        OPENING.edges[0],
        // Cross-cutting flows, in priority order.
        {
            id: edgeId(8),
            fromNodeId: null,
            toNodeId: END_DNC_ID,
            condition: { type: 'event', event: SCRATCHPAD_EVENTS.DO_NOT_CALL },
            position: 1,
        },
        {
            id: edgeId(10),
            fromNodeId: null,
            toNodeId: END_HANDOFF_ID,
            condition: { type: 'event', event: SCRATCHPAD_EVENTS.HANDOFF_REQUESTED },
            position: 2,
        },
        {
            id: edgeId(9),
            fromNodeId: null,
            toNodeId: END_HANDOFF_ID,
            condition: { type: 'event', event: SCRATCHPAD_EVENTS.NOT_INTERESTED },
            position: 3,
        },
        { ...OPENING.edges[1], position: 4 },
        {
            id: edgeId(2),
            fromNodeId: GREET_ID,
            toNodeId: DISCOVERY_ID,
            condition: { type: 'event', event: RUNTIME_EVENTS.TEXT_DELIVERED },
            position: 5,
        },
        {
            id: edgeId(11),
            fromNodeId: DISCOVERY_ID,
            toNodeId: SCHEDULE_ID,
            condition: allOf(formResult(DISCOVERY_ID, 'completed')),
            position: 6,
        },
        {
            id: edgeId(12),
            fromNodeId: SCHEDULE_ID,
            toNodeId: WRAP_UP_ID,
            condition: { type: 'event', event: 'meeting_booked' },
            position: 7,
        },
        {
            id: edgeId(16),
            fromNodeId: SCHEDULE_ID,
            toNodeId: END_HANDOFF_ID,
            condition: allOf(signalEquals(SCHEDULE_ID, 'wants_follow_up', true)),
            position: 8,
        },
        {
            id: edgeId(19),
            fromNodeId: WRAP_UP_ID,
            toNodeId: END_SIGN_OFF_ID,
            condition: allOf(signalEquals(WRAP_UP_ID, 'no_more_details', true)),
            position: 9,
        },
    ],
};
//# sourceMappingURL=new-owner-lead-template.js.map